/**
 * Single source of the `session_id` sent to the Ana (product-analytics) API,
 * shared by every web platform: a UUID v4 kept in the `ana_identifier` cookie.
 *
 * - Session cookie (no `Expires`/`Max-Age`): it dies when the browser closes.
 * - Set on the broadest domain the browser accepts (`dafiti.com.br`, not
 *   `www.dafiti.com.br`), so every subdomain and app reads the same value.
 * - Reused when a valid one already exists; created on the first event
 *   otherwise.
 *
 * The same logic lives in every web platform that sends events to Ana
 * (@dafiti/analytics-sdk, alice, catalog-front, checkout-front, login-front
 * and the web-frontend-*-xp apps): keep the copies in sync — they must all
 * agree on the cookie name, format and attributes.
 */
export const ANA_IDENTIFIER_COOKIE = 'ana_identifier';

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/;

/** Used only when the browser refuses the cookie, so one page keeps one id. */
let inMemoryIdentifier: string | null = null;

const readCookie = (): string => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${ANA_IDENTIFIER_COOKIE}=([^;]*)`),
  );
  return match?.[1] ?? '';
};

const generateUuidV4 = (): string => {
  // Absent in old browsers and non-secure contexts, despite the DOM typings.
  const crypto = globalThis.crypto as
    | (Crypto & { randomUUID?: () => string })
    | undefined;

  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof crypto?.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index++) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Candidate cookie domains, broadest first: `com.br`, `dafiti.com.br`,
 * `www.dafiti.com.br`, then host-only. The browser rejects public suffixes
 * (`com.br`), so the first one that reads back is the registrable domain —
 * no hardcoded per-country list needed. IPs and single-label hosts
 * (`localhost`) only take host-only cookies.
 */
const candidateDomains = (hostname: string): Array<string | undefined> => {
  const labels = hostname.split('.');
  if (labels.length < 2 || IPV4_PATTERN.test(hostname)) return [undefined];

  const domains: Array<string | undefined> = [];
  for (let index = labels.length - 2; index >= 0; index--) {
    domains.push(labels.slice(index).join('.'));
  }
  domains.push(undefined);

  return domains;
};

const writeCookie = (value: string, domain?: string) => {
  // Only UUIDs reach document.cookie: anything else could smuggle in cookie
  // attributes (`;`, `=`) if a future caller passes it through.
  if (!UUID_V4_PATTERN.test(value)) return;

  const attributes = ['path=/', 'SameSite=Lax'];
  if (domain) attributes.push(`domain=${domain}`);
  if (window.location.protocol === 'https:') attributes.push('Secure');

  document.cookie = `${ANA_IDENTIFIER_COOKIE}=${value}; ${attributes.join('; ')}`;
};

/** Returns the Ana session id, creating the cookie on first use. `null` on the server. */
export function getAnaIdentifier(): string | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  const existing = readCookie();
  if (UUID_V4_PATTERN.test(existing)) return existing;

  if (inMemoryIdentifier) return inMemoryIdentifier;

  const identifier = generateUuidV4();

  for (const domain of candidateDomains(window.location.hostname)) {
    writeCookie(identifier, domain);
    if (readCookie() === identifier) return identifier;
  }

  inMemoryIdentifier = identifier;
  return identifier;
}
